import { FC } from "react";
import { useSnackbar, VariantType } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { db_timestamp } from "@/utils/date-format";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import { Client } from "@/types";

import DefaultInquiryForm from "./DefaultInquiryForm";
import { createInquiry } from "../inquiries.api";
import {
  processCustomAttributesForCreation,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";
import type { InquiryFormData } from "../inquiries.types";

interface CreateInquiryFormProps {
  width?: number;
  onSubmit: (result?: unknown) => void;
  onCancel: () => void;
}

const CreateInquiryForm: FC<CreateInquiryFormProps> = ({
  width,
  onSubmit,
  onCancel,
}) => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const handleSubmit = async (data: InquiryFormData) => {
    try {
      const customAttributes = processCustomAttributesForCreation(
        data as Record<string, unknown>
      );
      const cleaned = extractCustomFieldsFromFormData(
        data as Record<string, unknown>
      );

      const linked = data.linked_client ?? null;
      const clientRecord =
        typeof linked === "object" && linked !== null
          ? ((linked.data ?? null) as Client | null)
          : null;

      const {
        linked_client: _linkedClient,
        client_id: _clientId,
        ...rest
      } = cleaned as InquiryFormData;
      void _linkedClient;
      void _clientId;

      const payload = {
        ...rest,
        client_id: data.client_id ?? null,
        inquiry_name: clientRecord?.name ?? "",
        inquiry_email: clientRecord?.email ?? null,
        inquiry_org: clientRecord?.organization ?? null,
        inquiry_date: db_timestamp(),
        inquiry_user: currentUser.user_id,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      const result = await createInquiry(payload);

      enqueueSnackbar("Inquiry Created.", {
        variant: "success" as VariantType,
      });

      queryClient.invalidateQueries({ queryKey: ["inquiries:list"] });
      queryClient.invalidateQueries({ queryKey: ["inquiries"] });

      onSubmit?.(result);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this inquiry. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle>Create Inquiry</FormTitle>
      <DefaultInquiryForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={onCancel}
        buttonProps={{ submitText: "Create Inquiry" }}
      />
    </FormWrapper>
  );
};

export default CreateInquiryForm;
